import { useState, useRef, useEffect, useMemo } from 'react';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import { FormattedMessage, useIntl } from 'react-intl';
import { styled, Typography } from '@mui/material';
import { debounce } from 'lodash';
import { useLocation, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

import MainCard from 'ui-component/cards/MainCard';
import Table from 'views/common/GlobalTable';
import SubSearchSection from 'views/common/SubSearchSection';
import NotFound from 'views/common/NotFound';
import Action from 'views/common/GlobalAction';
import GlobalDisableDialog from 'views/common/GlobalDisableDialog';
import { PaginationInfo, RequestParams } from 'types/pagination';
import { Container } from 'types/container';
import { getContainers, disableContainer } from 'services/ContainerService';
import { resetFilters } from 'store/slices/filter';
import { RootState } from 'store';
import { useDispatch, useSelector } from 'react-redux';
import { setPreviousPath } from 'store/slices/path';

const Text = styled('p')({
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center'
});

const ContainerList = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const filters = useSelector((state: RootState) => state.filters.filters);
    const previousPath = useSelector((state: RootState) => state.path.previousPath);
    const [searchText, setSearchText] = useState<string>();
    const componentRef = useRef<HTMLDivElement>(null);
    const [status, setStatus] = useState<number>(1); // 0: Ready, 1:Loading, 2: Not found
    const [paginationInfo, setPaginationInfo] = useState<PaginationInfo>();
    const [requestParams, setRequestParams] = useState<RequestParams>({
        page: 1,
        per_page: 10,
        sort: [{ field: 'updated_at', sort: 'desc' }],
        filters: undefined
    });
    const [openFilter, setOpenFilter] = useState<boolean>(false);
    const [openDisable, setOpenDisable] = useState<boolean>(false);
    const [openPreview, setOpenPreview] = useState<boolean>(false);
    const [currentId, setCurrentId] = useState<string>('');
    const [containers, setContainers] = useState<Container[]>([]);
    const [currentStatus, setCurrentStatus] = useState<number>(0); // 1: Disable, 0: Enable
    const [filtersReset, setFiltersReset] = useState(false);

    const navigate = useNavigate();

    const columns: GridColDef[] = [
        {
            field: 'id',
            flex: 1,
            sortable: paginationInfo?.sortable_fields?.includes('id'),
            renderHeader: () => (
                <Text>
                    <FormattedMessage id="id" />
                </Text>
            )
        },
        {
            field: 'title',
            flex: 1,
            sortable: paginationInfo?.sortable_fields?.includes('name'),
            renderHeader: () => (
                <Text>
                    <FormattedMessage id="container-name" values={{ indicator: '' }} />
                </Text>
            )
        },
        {
            field: 'display_name',
            flex: 1,
            sortable: paginationInfo?.sortable_fields?.includes('display_name'),
            renderHeader: () => (
                <Text>
                    <FormattedMessage id="display-name" values={{ indicator: '' }} />
                </Text>
            )
        },
        {
            field: 'container_type_id',
            flex: 1,
            sortable: paginationInfo?.sortable_fields?.includes('container_type_id'),
            renderHeader: () => (
                <Text>
                    <FormattedMessage id="container-type" values={{ indicator: '' }} />
                </Text>
            ),
            renderCell: (params) => <Typography>{params.row.type ? <FormattedMessage id={params.row.type.type} /> : '-'}</Typography>
        },
        {
            field: 'created_at',
            flex: 1,
            renderHeader: () => (
                <Text>
                    <FormattedMessage id="date" />
                </Text>
            ),
            renderCell: (params) => <> {params.row.created_at ? format(new Date(params.row.created_at || ''), 'dd.MM.yyyy') : '-'}</>
        },
        {
            field: 'deleted_at',
            flex: 1,
            sortable: paginationInfo?.sortable_fields?.includes('deleted_at'),
            renderHeader: () => (
                <Text>
                    <FormattedMessage id="status" />
                </Text>
            ),
            renderCell: (params) => (
                <Typography sx={{ color: 'primary.main', fontWeight: 500 }}>
                    <FormattedMessage id={`${params.row.deleted_at ? 'non-active' : 'active'}`} />
                </Typography>
            )
        },
        {
            field: 'updated_at',
            flex: 1,
            sortable: paginationInfo?.sortable_fields?.includes('updated_at'),
            renderHeader: () => (
                <Text>
                    <FormattedMessage id="creation-date" />
                </Text>
            ),
            renderCell: (params) => <> {params.row.updated_at ? format(new Date(params.row.updated_at || ''), 'dd.MM.yyyy') : '-'}</>
        },
        {
            field: 'action',
            width: 150,
            sortable: paginationInfo?.sortable_fields?.includes('action'),
            renderHeader: () => (
                <Text>
                    <FormattedMessage id="action" />
                </Text>
            ),
            renderCell: (params) => {
                const onClick = () => {
                    setCurrentId(params.row.id as string);
                    setCurrentStatus(params.row.deleted_at ? 0 : 1);
                };
                return (
                    <Action
                        setEdit={(e) => {
                            if (params.row.deleted_at) return;
                            onClick();
                            navigate(`${params.row.id}`);
                        }}
                        setDisable={(e) => {
                            onClick();
                            setOpenDisable(true);
                        }}
                        setDuplicate={(e: any) => {
                            if (params.row.deleted_at) return;
                            onClick();
                            navigate(`${params.row.id}?clone=true`);
                        }}
                        // setPreview={(e: any) => {
                        //     if (params.row.deleted_at) return;
                        //     onClick();
                        //     setOpenPreview(true);
                        // }}
                    />
                );
            }
        }
    ];

    const handleDisabled = async () => {
        setOpenDisable(false);
        await disableContainer(currentId);
        await refresh();
    };

    const refresh = async () => {
        setStatus(1);
        setRequestParams((r) => ({ ...r, filters: [] }));
    };

    useEffect(() => {
        dispatch(setPreviousPath(location.pathname));
    }, [location.pathname]);

    useEffect(() => {
        if (!previousPath?.includes('containers')) {
            dispatch(resetFilters());
            setFiltersReset(true);
        } else {
            setFiltersReset(false);
        }
    }, [previousPath, dispatch]);

    useEffect(() => {
        if (
            (previousPath?.includes('containers') && filters.length > 0) ||
            (previousPath === undefined && filters.length > 0) ||
            (filters.length > 0 && filtersReset)
        ) {
            setRequestParams((prev) => ({
                ...prev,
                filters
            }));
            setFiltersReset(false);
        }
    }, [previousPath, filters]);

    useEffect(() => {
        if (
            (previousPath?.includes('containers') && filters.length == 0) ||
            (previousPath === undefined && filters.length == 0 && filtersReset) ||
            (!previousPath?.includes('containers') && filters.length == 0 && filtersReset)
        ) {
            setRequestParams((prev) => ({
                ...prev,
                filters: []
            }));
        }
    }, [filters]);

    useEffect(() => {
        if (requestParams.filters !== undefined) {
            setStatus(1);
            getContainers(setContainers, requestParams, setPaginationInfo);
        }
    }, [requestParams]);

    useEffect(() => {
        if (searchText !== undefined) {
            setRequestParams((r) => ({
                ...r,
                filters: [...(r.filters?.filter((f) => f.name !== 'title') || []), { name: 'title', value: searchText }]
            }));
        }
    }, [searchText]);

    useEffect(() => {
        if (containers.length > 0 && paginationInfo) {
            setStatus(0);
        }
        if (containers.length === 0 && paginationInfo) {
            setStatus(2);
        }
    }, [containers, paginationInfo]);

    const onPageChange = (newPage: number) => {
        setRequestParams({ ...requestParams, page: newPage });
        setStatus(1);
        // getContainers(setContainers, { ...requestParams, page: newPage }, setPaginationInfo);
    };

    const onPageSizeChange = (per_page: number) => {
        setRequestParams({ ...requestParams, per_page, page: 1 });
        setStatus(1);
        // getContainers(setContainers, { ...requestParams, per_page, page: 1 }, setPaginationInfo);
    };

    const debouncedSearchTextChange = useMemo(() => debounce(setSearchText, 300), []);

    const onSortModelChange = (model: GridSortModel) => {
        setRequestParams({ ...requestParams, sort: model });
    };

    return (
        <MainCard sx={{ minHeight: 'calc(100vh - 240px)' }}>
            <SubSearchSection
                filters={requestParams.filters}
                // setFilters={(filters) => setRequestParams({ ...requestParams, filters })}
                filter={openFilter}
                setFilter={setOpenFilter}
                availableFilters={paginationInfo?.available_filters}
                setSearchText={debouncedSearchTextChange}
                printRef={componentRef}
                label="container-management"
                columnCount={columns.length - 1} // Print layout needed
                setAdd={() => navigate('add')}
            />
            {status !== 0 ? (
                <>
                    <NotFound status={status} columnsLoaded={columns.length} tryAgain={refresh} />
                </>
            ) : (
                <>
                    <div ref={componentRef}>
                        <Table
                            onCellClick={(params) => {
                                if (params.field !== 'action') {
                                    if (params.field !== 'action') navigate(`${params.id}`);
                                }
                            }}
                            sortModel={requestParams.sort}
                            onSortModelChange={onSortModelChange}
                            onPageSizeChange={onPageSizeChange}
                            page={requestParams.page}
                            paginationInfo={paginationInfo}
                            onPageChange={onPageChange}
                            rows={containers}
                            columns={columns}
                        />
                    </div>
                </>
            )}
            <GlobalDisableDialog
                open={openDisable}
                onClose={() => setOpenDisable(false)}
                onDisable={handleDisabled}
                title1={useIntl().formatMessage({
                    id: currentStatus === 0 ? 'are-you-sure-you-need-to-make' : 'are-you-sure-you-want-to-turn'
                })}
                coloredText={useIntl().formatMessage({ id: 'container-with-id' }, { id: currentId })}
                title2={useIntl().formatMessage({ id: currentStatus === 0 ? 'active?' : 'container-disabled?' })}
                labelButton={useIntl().formatMessage({ id: currentStatus === 0 ? 'turn to active' : 'inactive' })}
            />
        </MainCard>
    );
};

export default ContainerList;
